import type { GeoJsonProperties } from 'geojson'
import Supercluster from 'supercluster'
import type { Listing } from '~/models'
import { fmtAddress, PROPERTY_TYPES_COLORS } from '~/utils'

export type ListingMarkerShape = 'lease' | 'sale' | 'sale-lease'

export type ListingMarkerColorName =
  | 'amber'
  | 'orange'
  | 'pink'
  | 'slate'
  | 'emerald'
  | 'indigo'
  | 'fuchsia'
  | 'cyan'
  | 'stone'
  | 'red'

export type ListingMarkerIconId =
  | 'orange-dark-business'
  | 'selected-business'
  | `selected-${ListingMarkerShape}`
  | `${ListingMarkerColorName}-${ListingMarkerShape}`

export type ListingMarkerIconDefinition = {
  anchorY: number
  height: number
  id: ListingMarkerIconId
  url: string
  width: number
}

export type ListingPointProperties = {
  _id: string
  address: string
  color: [number, number, number]
  iconType: ListingMarkerShape
  isBusinessListing: boolean
  lat: number
  lng: number
  name: string
  propertyTypeList: string[]
  primaryPropertyType: string
  propertyTypes: string
  slug: string
  status: string
  thumbUrl?: string
  type: string
}

export type ListingPointFeature =
  Supercluster.PointFeature<ListingPointProperties>

export type ListingClusterFeature =
  Supercluster.ClusterFeature<GeoJsonProperties>

export type ClusterOrListingPointFeature =
  | ListingClusterFeature
  | ListingPointFeature

export type MapListingFeatureInput = Pick<
  Listing,
  | '_id'
  | 'images'
  | 'isBusinessListing'
  | 'location'
  | 'locationVisibility'
  | 'name'
  | 'property'
  | 'propertyTypes'
  | 'slug'
  | 'status'
  | 'type'
> & {
  image?: {
    thumbUrl?: string
  }
}

const DEFAULT_MARKER_COLOR_NAME: ListingMarkerColorName = 'red'

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '')
  const parsed = Number.parseInt(normalized, 16)

  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255]
}

function getListingColor(
  listing: MapListingFeatureInput
): [number, number, number] {
  if (listing.isBusinessListing === true) {
    return [194, 65, 12]
  }

  const primaryPropertyType = listing.propertyTypes[0] ?? 'Other'
  const match = PROPERTY_TYPES_COLORS.find(
    ({ propertyType }) => propertyType === primaryPropertyType
  )

  if (match) return hexToRgb(match.color)
  if (listing.type === 'sale') return [220, 38, 38]
  if (listing.type === 'lease') return [37, 99, 235]

  return [147, 51, 234]
}

function getListingIconType(
  listing: MapListingFeatureInput
): ListingMarkerShape {
  if (listing.type === 'sale') return 'sale'
  if (listing.type === 'lease') return 'lease'

  return 'sale-lease'
}

function getMarkerColorName(
  properties: ListingPointProperties
): ListingMarkerColorName {
  const match = PROPERTY_TYPES_COLORS.find(
    ({ propertyType }) => propertyType === properties.primaryPropertyType
  )

  return (
    (match?.value as ListingMarkerColorName | undefined) ??
    DEFAULT_MARKER_COLOR_NAME
  )
}

function getListingAddress(listing: MapListingFeatureInput) {
  if (listing.isBusinessListing === true) {
    return fmtAddress(listing.property, {
      locationVisibility: listing.locationVisibility,
    })
  }

  return [listing.property?.address, listing.property?.address2]
    .filter(Boolean)
    .join(' ')
}

function getListingThumbUrl(listing: MapListingFeatureInput) {
  return listing.image?.thumbUrl ?? listing.images?.[0]?.thumbUrl
}

export function getListingCoordinates(
  feature: ClusterOrListingPointFeature
): [number, number] {
  const [lng, lat] = feature.geometry.coordinates

  return [lng, lat]
}

export function getListingLatLng(
  feature: ClusterOrListingPointFeature
): google.maps.LatLngLiteral {
  const [lng, lat] = feature.geometry.coordinates

  return { lat, lng }
}

export function normalizeListingsToPointFeatures(
  listings: MapListingFeatureInput[]
): ListingPointFeature[] {
  return listings
    .filter((listing) => {
      const coordinates = listing.location?.coordinates

      return (
        Array.isArray(coordinates) &&
        coordinates.length === 2 &&
        typeof coordinates[0] === 'number' &&
        typeof coordinates[1] === 'number'
      )
    })
    .map((listing) => {
      const [lng, lat] = listing.location.coordinates as [number, number]

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
        properties: {
          _id: listing._id,
          address: getListingAddress(listing),
          color: getListingColor(listing),
          iconType: getListingIconType(listing),
          isBusinessListing: listing.isBusinessListing === true,
          lat,
          lng,
          name: listing.name,
          propertyTypeList: listing.propertyTypes,
          primaryPropertyType: listing.propertyTypes[0] ?? 'Other',
          propertyTypes: listing.propertyTypes.join(', '),
          slug: listing.slug,
          status: listing.status,
          thumbUrl: getListingThumbUrl(listing),
          type: listing.type,
        },
      }
    })
}

export function getListingMarkerIconId(
  properties: ListingPointProperties
): ListingMarkerIconId {
  if (properties.isBusinessListing) {
    return 'orange-dark-business'
  }

  return `${getMarkerColorName(properties)}-${properties.iconType}`
}

export function getSelectedListingMarkerIconId(
  properties: ListingPointProperties
): ListingMarkerIconId {
  if (properties.isBusinessListing) {
    return 'selected-business'
  }

  return `selected-${properties.iconType}`
}

export function getListingMarkerIconDefinition(
  iconId: ListingMarkerIconId
): ListingMarkerIconDefinition {
  if (iconId === 'orange-dark-business' || iconId === 'selected-business') {
    return {
      anchorY: iconId === 'selected-business' ? 61 : 100,
      height: iconId === 'selected-business' ? 122 : 199,
      id: iconId,
      url: `/markers/${iconId}.png`,
      width: iconId === 'selected-business' ? 128 : 210,
    }
  }

  if (
    iconId === 'selected-sale' ||
    iconId === 'selected-lease' ||
    iconId === 'selected-sale-lease'
  ) {
    return {
      anchorY: 64,
      height: 129,
      id: iconId,
      url: `/markers/${iconId}.png`,
      width: 128,
    }
  }

  return {
    anchorY: 100,
    height: 200,
    id: iconId,
    url: `/markers/${iconId}.png`,
    width: 200,
  }
}

export function isListingClusterFeature(
  feature: ClusterOrListingPointFeature
): feature is ListingClusterFeature {
  return 'cluster' in feature.properties && feature.properties.cluster === true
}
